import { UploadOutlined, ZoomInOutlined } from "@ant-design/icons";
import { Button, Form, Input, InputNumber, Modal, Select, Tag, Upload, message } from "antd";
import { AktDatePicker } from "components/aktDatePicker";
import {
  useCreateDisputeMutation,
  useFetchDisputeCategoriesQuery,
  useUpdateAccountMutation,
  useUpdateDisputeMutation,
} from "features/debtorAccountsTable/agencyPortal/debtorAccountsAPI";
import { useLazyFetchDisputePreviewQuery } from "features/disputeManagementTable/disputeTableAPI";
import { usePostDocumentsMutation } from "features/documentsTable/agencyPortal/documentsAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { useFetchAgencyQuery } from "features/basicInfo/agencyPortal/agencySettingsAPI";
import moment from "moment-timezone";
import { useState } from "react";
import styled from "styled-components";
import { camelToWords, snakeToCamelCase } from "common/utils";
import { DATE_TIME_FORMAT } from "common/constants";
import { BCCC_CODES } from "features/debtorAccountsTable/components/creditBureauSettingsModal";

const StyledTag = styled(Tag)`
  margin-left: 5px;
  text-transform: uppercase;
`;

const StyledDragger = styled(Upload.Dragger)`
  margin: 12px 0;
  cursor: pointer;
`;

const StyledP = styled.p`
  color: grey;
  font-size: 12px;
  font-style: italic;
`;

function ManageDisputeFromAccountModal({
  dispute,
  title,
  open,
  onOk,
  onCancel,
  account,
  debtorId,
}) {
  // const { isCreditorUserType } = useUserType();
  const { data: categories } = useFetchDisputeCategoriesQuery({ creditorId: account?.creditorId });
  const { data: constants } = useFetchBackendConstantsQuery();
  const [createDispute, { isLoading: createLoading }] = useCreateDisputeMutation();
  const [updateDispute, { isLoading: updateLoading }] = useUpdateDisputeMutation();
  const [updateAccountDebt, { isLoading: updateAccountLoading }] = useUpdateAccountMutation();
  const [postDocuments] = usePostDocumentsMutation();
  const [fetchDisputePreview] = useLazyFetchDisputePreviewQuery();
  const [form] = Form.useForm();
  const { data: agencySettings } = useFetchAgencyQuery();
  const isBcccRequired = agencySettings?.isDisputeBcccRequired;

  const disabledDate = (current) => {
    // Can not select days tomorrow and later
    return current && current > moment().endOf("day");
  };

  const [fileList, setFileList] = useState([]);

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }

    return e?.fileList;
  };

  const draggerProps = {
    multiple: false,
    showUploadList: {
      showDownloadIcon: false,
    },
    onRemove: () => {
      setFileList([]);
    },
    beforeUpload: (file) => {
      setFileList([file]);
      return false;
    },
    fileList,
  };

  const onDispute = async () => {
    const values = await form.validateFields();
    const disputeAction = dispute?.status ? updateDispute : createDispute;
    const result = await disputeAction({
      accountId: account?.id,
      disputeId: dispute?.id,
      ...values.dispute,
    });
    await updateAccountDebt({
      accountId: account?.id,
      complianceConditionCode: values?.complianceConditionCode,
    });
    if ("data" in result) {
      if (values?.file) {
        const disputeDocumentType = constants.documentTypes?.find(
          (documentType) => documentType.value === "dispute",
        )?.value;
        // We only support sending a single file
        const documentUploadResult = await postDocuments({
          debtorId,
          disputeId: result?.data?.result?.id,
          file: values?.file[0],
          filename: values?.file[0].name,
          type: disputeDocumentType,
        });
        if ("data" in documentUploadResult) {
          message.success(`${values?.file[0].name} file uploaded successfully`);
        }
      }

      message.success(
        `Dispute has been successfully ${dispute?.status ? "updated" : "created"} for this account`,
      );
      setFileList([]);
      onOk(); // Close the modal using the parent callback
    }
  };

  const initialValues = {
    dispute: {
      ...dispute,
      amount: dispute?.amount ?? account?.originalTotal,
    },
    complianceConditionCode: account?.complianceConditionCode || null,
  };

  return (
    <Modal
      maskClosable={false}
      title={
        <>
          {title}
          {dispute?.status === "resolved" ? <StyledTag color="green">resolved</StyledTag> : null}
          {dispute?.status && dispute?.status !== "resolved" ? (
            <StyledTag color="red">disputed</StyledTag>
          ) : null}
        </>
      }
      open={open}
      okButtonProps={{
        loading: createLoading || updateLoading || updateAccountLoading,
      }}
      footer={[
        dispute?.dataSource === "e_oscar" && (
          <Button
            key="preview"
            color="default"
            onClick={async () => {
              const result = await fetchDisputePreview({ id: dispute?.id });
              if ("data" in result) {
                Modal.info({
                  title: "Preview",
                  width: "60vw",
                  okText: "Close",
                  onOk: () => {
                    Modal.destroyAll();
                  },
                  content: result.data?.htmlContent ? (
                    <div
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: result.data?.htmlContent }}
                    />
                  ) : (
                    <div>No preview available</div>
                  ),
                });
              }
            }}
          >
            <ZoomInOutlined />
            Preview
          </Button>
        ),
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={onDispute}
          loading={createLoading || updateLoading || updateAccountLoading}
        >
          OK
        </Button>,
      ]}
      okText={!dispute?.status ? "Create Dispute" : "Update"}
      onCancel={onCancel}
    >
      <Form
        layout="vertical"
        initialValues={initialValues}
        labelAlign="left"
        form={form}
        autoComplete="off"
      >
        <Form.Item
          label="Amount in Dispute"
          name={["dispute", "amount"]}
          rules={[{ required: true }]}
        >
          <InputNumber min={0} prefix="$" controls={false} step={0.01} />
        </Form.Item>
        <Form.Item label="Category" name={["dispute", "categoryTagIds"]}>
          <Select
            allowClear
            mode="multiple"
            options={categories?.map((each) => ({
              label: each.name,
              value: each.id,
            }))}
          />
        </Form.Item>
        <Form.Item label="Status" name={["dispute", "status"]} rules={[{ required: true }]}>
          <Select
            popupMatchSelectWidth
            options={constants?.disputeStatuses.map((each) => ({
              label: each.display,
              value: each.value,
            }))}
          />
        </Form.Item>
        <Form.Item label="Channel Source" name={["dispute", "channelSource"]}>
          <Select
            allowClear
            popupMatchSelectWidth={false}
            options={constants?.disputeChannelSources?.map((each) => ({
              label: each.display,
              value: each.value,
            }))}
          />
        </Form.Item>
        {dispute && (
          <Form.Item label="Data Source">
            {dispute?.dataSource ? camelToWords(snakeToCamelCase(dispute.dataSource)) : "-"}
          </Form.Item>
        )}
        {dispute?.dataSource === "e_oscar" && (
          <>
            <Form.Item label="External ID">{dispute?.externalId || "-"}</Form.Item>
            <Form.Item name={["dispute", "eOscarStatusCode"]} label="E-Oscar Status Code">
              <Select
                popupMatchSelectWidth={false}
                placeholder="Select an option"
                options={constants?.eOscarDisputeStatusCodes?.map((each) => ({
                  label: each.display,
                  value: each.value,
                }))}
              />
            </Form.Item>
            <Form.Item label="E-Oscar Code Updated At">
              {dispute?.eOscarStatusCodeUpdatedAt
                ? moment(dispute?.eOscarStatusCodeUpdatedAt).format(DATE_TIME_FORMAT)
                : "-"}
            </Form.Item>
          </>
        )}
        <Form.Item
          label="Date Received"
          name={["dispute", "receivedDate"]}
          rules={[{ required: true }]}
        >
          <AktDatePicker format="YYYY-MM-DD" disabledDate={disabledDate} />
        </Form.Item>
        <Form.Item label="Date Resolved" name={["dispute", "resolvedDate"]}>
          <AktDatePicker format="YYYY-MM-DD" disabledDate={disabledDate} />
        </Form.Item>
        {/* The Bureau Compliance (BCCC) Code only shows if the dispute is being created.
          Since an account only has one BCCC Code, we only give the option to change it if the
          dispute is being created. If they want to change it, they can update the account's BCCC
          Code in the 'Configure Credit Bureau Settings' action.
        */}
        {!dispute?.status ? (
          <Form.Item
            name={["complianceConditionCode"]}
            label="Bureau Compliance Condition Code"
            tooltip="The account's Bureau Compliance Condition Code to report to the bureau."
            rules={
              isBcccRequired
                ? [{ required: true, message: "Bureau Compliance Condition Code is required" }]
                : []
            }
          >
            <Select
              popupMatchSelectWidth={false}
              placeholder="Select an option"
              options={BCCC_CODES}
              allowClear={!isBcccRequired}
            />
          </Form.Item>
        ) : null}
        <Form.Item label="Notes" name={["dispute", "notes"]}>
          <Input.TextArea />
        </Form.Item>
        <Form.Item name="file" valuePropName="fileList" getValueFromEvent={normFile}>
          <StyledDragger {...draggerProps}>
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload documentation
            </p>
          </StyledDragger>
        </Form.Item>
        <StyledP>Note: Please visit the Documents tab to see previously uploaded files.</StyledP>
      </Form>
    </Modal>
  );
}

export default ManageDisputeFromAccountModal;
